import { URL } from "../utils/constants";
import { fetchWithApiKey } from "./fetchWithApiKey";

/**
 * Servicio de autenticación que maneja la comunicación con el backend
 * El login es manejado externamente por functions.js
 */
class AuthService {
  constructor() {
    this.baseURL = URL;
    this.token = localStorage.getItem('sanctum_token');
  }

  /**
   * Verifica si el usuario está autenticado consultando al backend
   */
  async checkAuthStatus() {
    console.group('🔍 authService.checkAuthStatus');
    
    try {
      const token = this.getToken();
      console.log('Token obtenido:', token ? `${token.substring(0, 20)}...` : 'null');
      
      if (!token) {
        console.warn('❌ No hay token disponible');
        console.groupEnd();
        return false;
      }

      console.log('🚀 Haciendo petición a /api/me...');
      
      const userData = await fetchWithApiKey(`${this.baseURL}me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('✅ Respuesta exitosa:', userData);
      
      // Actualizar user_data en localStorage si es necesario
      if (userData.user) {
        localStorage.setItem('user_data', JSON.stringify(userData.user));
        console.log('💾 Datos de usuario actualizados en localStorage');
      }
      console.groupEnd();
      return userData;
      
    } catch (error) {
      console.error('❌ Error verificando estado de autenticación:', error);
      // Si es error 401, el token ha expirado
      if (error.message.includes('401') || error.message.includes('expirado')) {
        this.removeToken();
      }
      console.groupEnd();
      return false;
    }
  }

  /**
   * Obtener datos del usuario desde localStorage
   */
  getUserData() {
    try {
      const userData = localStorage.getItem('user_data');
      console.log('💾 getUserData:', userData ? 'Datos encontrados' : 'Sin datos');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('❌ Error parsing user data:', error);
      return null;
    }
  }

  /**
   * Logout del usuario
   */
  async logout() {
    try {
      const token = this.getToken();
      if (token) {
        await fetchWithApiKey(`${this.baseURL}logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      this.removeToken();
    }
  }

  /**
   * Obtener el token del localStorage
   */
  getToken() {
    return localStorage.getItem('sanctum_token');
  }

  /**
   * Guardar el token en localStorage (usado por el servicio externo)
   */
  setToken(token) {
    localStorage.setItem('sanctum_token', token);
    this.token = token;
  }

  /**
   * Eliminar el token y datos de usuario del localStorage
   */
  removeToken() {
    localStorage.removeItem('sanctum_token');
    localStorage.removeItem('user_data');
    this.token = null;
  }

  /**
   * Verificar si el usuario tiene un token válido por UID
   * @param {number} uid - ID del usuario
   * @returns {Promise<Object|false>} - Token y datos del usuario o false
   */
  async checkUserTokenByUID(uid) {
    console.group('🔍 authService.checkUserTokenByUID');
    console.log('UID recibido:', uid);
    
    try {
      console.log('🚀 Verificando token para usuario ID:', uid);
      console.log()
      const tokenData = await fetchWithApiKey(`${this.baseURL}login-user/${uid}/token`, {
        method: 'GET'
      });

      console.log('✅ Token encontrado para usuario:', tokenData);
        
      // Guardar token y datos del usuario
      if (tokenData.token) {
        this.setToken(tokenData.token);
        localStorage.setItem('user_data', JSON.stringify(tokenData.user));

        console.log('💾 Datos de usuario guardados en localStorage');
        console.log('💾 Token guardado en localStorage');
        console.log('🚀 Verificando token con el backend...');
        const tokenVerification = await fetchWithApiKey(`${this.baseURL}verify-token`, {
          method: 'POST'
        });
        console.log('✅ Token verificado exitosamente:', tokenVerification);
        console.groupEnd();
        return tokenData;
      }
    } catch (error) {
        console.error(error)
      console.error('❌ Error verificando token por UID:');
      
      // Si es error 404, el usuario no tiene token válido
      if (error.message && error.message.includes('404')) {
        console.warn('⚠️ Usuario no tiene token válido disponible');
      }
      
      console.groupEnd();
      return false;
    }
  }

  /**
   * Verificar si hay un token almacenado
   */
  hasToken() {
    const hasToken = !!this.getToken();
    console.log('🔑 hasToken:', hasToken);
    return hasToken;
  }

  /**
   * Obtener headers de autorización para peticiones
   */
  getAuthHeaders() {
    const token = this.getToken();
    return token ? {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    } : {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }
}

// Exportar una instancia singleton
export const authService = new AuthService();
export default authService;